import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * ShopFilled component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconShopFilled extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1 2.75C1 2.33579 1.33579 2 1.75 2H4.93572C5.76555 2 6.48129 2.58281 6.64942 3.39544L6.9814 5H21.5451C22.6408 5 23.467 5.99553 23.2651 7.0725L22.1401 13.0725C21.9849 13.9002 21.2622 14.5 20.4201 14.5H8.94692L9.31947 16.3007C9.34349 16.4167 9.44573 16.5 9.56428 16.5H19.25C19.6642 16.5 20 16.8358 20 17.25C20 17.6642 19.6642 18 19.25 18H9.56428C8.73444 18 8.01871 17.4172 7.85058 16.6046L5.18053 3.69935C5.15651 3.58326 5.05427 3.5 4.93572 3.5H1.75C1.33579 3.5 1 3.16421 1 2.75ZM18.5 23.0759C19.3284 23.0759 20 22.4044 20 21.5759C20 20.7475 19.3284 20.0759 18.5 20.0759C17.6716 20.0759 17 20.7475 17 21.5759C17 22.4044 17.6716 23.0759 18.5 23.0759ZM10 23.076C10.8284 23.076 11.5 22.4044 11.5 21.576C11.5 20.7476 10.8284 20.076 10 20.076C9.17159 20.076 8.50002 20.7476 8.50002 21.576C8.50002 22.4044 9.17159 23.076 10 23.076Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-shop-filled') === undefined) {
  customElements.define('cat-icon-shop-filled', BlocksIconShopFilled);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-shop-filled': BlocksIconShopFilled;
  }
}
